// Session-scoped attribution helpers.
//
// Captures the visitor's first-touch attribution the FIRST time they land
// on the site during a browser session, then makes it available to every
// analytics/beacon/registration call for the remainder of that session.
//
// Reuses the existing UTM plumbing: `ref` sits alongside utm_source/medium
// /campaign in every payload. It is NOT surfaced in any public URL other
// than the initial `?ref=<code>` query parameter used to distribute
// private links (e.g. s01 → IAGP, s02 → AIPSIM). The public code is
// intentionally opaque — the Back Office resolves it to the human label.
//
// Storage: sessionStorage (cleared when the tab closes). No cookies, no
// localStorage, no cross-session tracking.
const KEY = "sd_attribution_v1";

const REF_KEYS = ["ref"];
const UTM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
];

function readCurrentQuery() {
  try {
    const p = new URLSearchParams(window.location.search || "");
    const out = { ref: null, utm: {} };
    for (const k of REF_KEYS) {
      const v = (p.get(k) || "").trim();
      if (v) out.ref = v.slice(0, 64);
    }
    for (const k of UTM_KEYS) {
      const v = (p.get(k) || "").trim();
      if (v) out.utm[k] = v.slice(0, 128);
    }
    return out;
  } catch {
    return { ref: null, utm: {} };
  }
}

/**
 * Returns the visitor's active attribution, capturing the first-touch
 * values on the very first call of the session (idempotent afterwards).
 *
 * Shape: { ref: string|null, utm: {utm_source?, utm_medium?, ...} }
 */
export function getAttribution() {
  if (typeof window === "undefined") return { ref: null, utm: {} };
  try {
    const cached = window.sessionStorage.getItem(KEY);
    if (cached) {
      try { return JSON.parse(cached); } catch { /* fallthrough */ }
    }
  } catch { /* private-mode */ }

  const current = readCurrentQuery();
  const hasAny = current.ref || Object.keys(current.utm).length > 0;
  if (hasAny) {
    try { window.sessionStorage.setItem(KEY, JSON.stringify(current)); } catch {}
    return current;
  }
  return { ref: null, utm: {} };
}

/** Convenience: single flat object for payloads that already use utm_* keys. */
export function attributionForPayload() {
  const a = getAttribution();
  return {
    ref: a.ref || null,
    utm_source: a.utm.utm_source || null,
    utm_medium: a.utm.utm_medium || null,
    utm_campaign: a.utm.utm_campaign || null,
    utm_content: a.utm.utm_content || null,
    utm_term: a.utm.utm_term || null,
  };
}
